import { Edit as EditIcon, Add as PlusIcon, Block as BlockIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { DataGridPro, GridActionsCellItem, GridColumns, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { ProgressButton, WO2Select } from 'src/common/components';
import { useConfirmation } from 'src/common/components/dialogs/confirmationService';
import { FeeNatureEnum, FeeValueMethodEnum } from '../../store/enums';
import { Props } from '../container';
import { CreateTemplateRequest, UpdateTemplateRequest, GrandfatherTemplateRequest, Template, TemplateThunkResponse } from '../store';
import { STATUS_COLORS } from '../store/constants';
import { GrandfatherTemplateDialog } from './grandfatherTemplateDialog';
import { TemplateDialog } from './TemplateDialog/templateDialog';
import { DateTimeFormat, getLocalDateTime } from 'src/common';

export const Fees = ({
  match,
  setAfslId,
  afslId,
  fetchAfsls,
  afsls,
  templates,
  fetchTemplates,
  duplicateTemplate,
  grandfatherTemplate,
  createTemplate,
  updateTemplate,
  fetchTemplatesProgress,
  duplicateTemplateProgress,
  grandfatherTemplateProgress,
  manageTemplateProgress,
}: Props): JSX.Element => {
  const confirm = useConfirmation();
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [grandfatherTemplateDialogModel, setGrandfatherTemplateDialogModel] = useState<GrandfatherTemplateRequest | null>(null);
  const [templateDialogModel, setTemplateDialogModel] = useState<CreateTemplateRequest | null>(null);

  useEffect(() => {
    setAfslId(+match.params.afslId);
  }, [match.params.afslId]);

  useEffect(() => {
    fetchAfsls();
  }, [fetchAfsls]);

  useEffect(() => {
    if (afslId) {
      fetchTemplates({ afslId });
    }
  }, [afslId]);

  const isLoading = () => {
    return fetchTemplatesProgress.isLoading ||
      duplicateTemplateProgress.isLoading ||
      grandfatherTemplateProgress.isLoading ||
      manageTemplateProgress.isLoading
  }

  const duplicateRow = useCallback(
    (feeId: number) => {
      confirm({
        title: 'Duplicate template',
        description: 'This action will duplicate the template, but it will not copy or link any clients currently using it.',
        description2: 'Are you sure you wish to duplicate this template?',
      }).then(() => {
        afslId && duplicateTemplate({ afslId, feeId });
      });
    },
    [duplicateTemplate, afslId]
  );

  const setGrandfatherTemplateRequest = useCallback(
    (template: Template) => {
      setGrandfatherTemplateDialogModel(
        {
          afslId: afslId || 0,
          feeId: template.id,
          grandfatherDate: template.grandfathered_from
        }
      )
    }, [afslId, setGrandfatherTemplateDialogModel]);

  const setTemplateRequest = useCallback(
    (template: Template | null) => {
      const request: CreateTemplateRequest = {
        afslId: afslId || 0,
        subCategoryId: template?.nfe_fee_sub_category_id || null,
        feeName: template?.fee_name || '',
        displayFrequencyId: template?.nfe_display_frequency_id || null,
        feeNatureId: template?.nfe_fee_nature_id || null,
        excludeAssetClassIds: template?.nfe_fee_valuation_basis?.asset_exclusions || [],
        includeAssetClassIds: template?.nfe_fee_valuation_basis?.asset_inclusions || [],
        feeTiers: template?.nfe_fee_recipients[0]?.nfe_fee_to_recipients?.map((feeToRecipient) => { 
            return {
              from: +feeToRecipient.from,
              to: +feeToRecipient.to,
              valueWithGst: feeToRecipient.valueWithGst,
              valueMethod: feeToRecipient.value_method
            }
        }) || [
          {
            from: 0,
            to: 0,
            valueWithGst: 0,
            valueMethod: FeeValueMethodEnum.Percent.name
          }
        ]
      }
      
      if (template !== null) {
        (request as UpdateTemplateRequest).feeId = template.id
      }

      setTemplateDialogModel(request)
    }, [afslId, setTemplateDialogModel]);

  const columns: GridColumns = [
    {
      field: 'fee_code',
      headerName: 'CODE',
      flex: 4,
    },
    {
      field: 'fee_name',
      headerName: 'NAME',
      flex: 4,
    },
    {
      field: 'nfe_fee_nature_id',
      headerName: 'Type',
      flex: 4,
      renderCell: (params: GridRenderCellParams<Template>) => {
        return FeeNatureEnum.getById(params.row.nfe_fee_nature_id)?.displayName;
      }
    },
    {
      field: 'mda_clients_using',
      headerName: 'CLIENTS USING',
      flex: 4,
    },
    {
      field: 'available_from',
      headerName: 'OPENED',
      flex: 4,
      renderCell: (params: GridRenderCellParams<Template>) => {
        if (params.row.available_from === null) return '';
        return getLocalDateTime(params.row.available_from, DateTimeFormat.Short);
      }
    },
    {
      field: 'grandfathered_from',
      headerName: 'CLOSED',
      flex: 4,
      renderCell: (params: GridRenderCellParams<Template>) => {
        if (params.row.grandfathered_from === null) return '';
        return getLocalDateTime(params.row.grandfathered_from, DateTimeFormat.Short);
      }
    },
    {
      field: 'mda_status',
      headerName: 'STATUS',
      flex: 4,
      renderCell: (params: GridRenderCellParams<Template>) => {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={params.row.mda_status} color={STATUS_COLORS[params.row.mda_status]} />
          </Box>
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      sortable: false,
      width:120,
      getActions: React.useCallback(
        (params: GridRowParams<Template>) => {
          return [
            <GridActionsCellItem
              key="1"
              icon={<BlockIcon />}
              label="Grandfather"
              title="Close this template to new clients"
              onClick={() => {
                setGrandfatherTemplateRequest(params.row);
              }}
              color="primary"
            />,
            <GridActionsCellItem
              key="1"
              icon={<CopyIcon />}
              label="Duplicate"
              title="Duplicate template"
              onClick={() => {
                duplicateRow(params.row.id);
              }}
              color="primary"
            />,
            <GridActionsCellItem
              key="1"
              icon={<EditIcon />}
              label="Edit"
              title={params.row["can_edit?"] ? "Edit this template" : "Cannot edit active tempaltes"}
              onClick={() => setTemplateRequest(params.row)}
              disabled={!params.row["can_edit?"]}
              color="primary"
            />
          ];
        },
        [setGrandfatherTemplateRequest, setTemplateRequest, duplicateRow, afslId]
      ),
    },
  ];

  return (
    <Box style={{ paddingTop: '20px' }}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant="h2">Templates</Typography>
      </Box>
      <Stack direction="row" width={'100%'}>
        <Box display={'flex'} justifyContent={'space-between'} paddingTop="30px" paddingRight={'10px'} width="70%">
          {afsls.length > 1 && (
            <FormControl variant="outlined" style={{ width: '260px' }}>
              <InputLabel id="afsl-select-label">Afsl</InputLabel>
              <WO2Select
                labelId="afsl-select-label"
                id="afsl-select"
                value={afslId || ''}
                onChange={(event) => {
                  setAfslId(+`${event.target.value}`);
                }}
                label={'Fee'}
                style={{ width: '600px' }}
              >
                {afsls.map((item, index) => (
                  <MenuItem key={index} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </WO2Select>
            </FormControl>
          )}
        </Box>
        <Box display={'flex'} justifyContent={'space-between'} paddingTop="30px" paddingRight={'10px'} width="30%" style={{ justifyContent: 'end' }}>
          <ProgressButton
            color={'primary'}
            style={{ borderRadius: 24, height: 48 }}
            disableElevation
            disabled={!afslId || fetchTemplatesProgress.isLoading}
            progress={manageTemplateProgress}
            onClick={() => setTemplateRequest(null)}
            variant={'contained'}
            value="SaveReportingParameters"
            startIcon={PlusIcon}
          >
            ADD TEMPLATE
          </ProgressButton>
        </Box>
      </Stack>
      <div style={{ width: '100%', paddingTop: '30px' }}>
        <DataGridPro
          rows={templates.map((t, index) => ({ index: index, ...t }))}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'fee_name', sort: 'asc' }],
            },
          }}
          loading={isLoading()}
          autoHeight
          pagination
          pageSize={pageSize}
          rowsPerPageOptions={[5, 10, 20]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          disableSelectionOnClick
        />
      </div>

      {!!grandfatherTemplateDialogModel && (
        <GrandfatherTemplateDialog
          model={grandfatherTemplateDialogModel}
          onClose={() => { setGrandfatherTemplateDialogModel(null); }}
          onConfirm={async (payload: GrandfatherTemplateRequest) => {
            grandfatherTemplate(payload);
          }}
          confirmProgress={grandfatherTemplateProgress}
        />
      )}

      {!!templateDialogModel && (
        <TemplateDialog
          model={templateDialogModel}
          onClose={() => { setTemplateDialogModel(null); }}
          onConfirm={async (payload: CreateTemplateRequest | UpdateTemplateRequest) => {
            if ('feeId' in payload) {
               return (await updateTemplate(payload)).payload as TemplateThunkResponse;
            } else {
              return (await createTemplate(payload)).payload as TemplateThunkResponse;
            }
          }}
          confirmProgress={manageTemplateProgress}
        />
      )}
    </Box>
  );
};
